// ─── HAFAS Service ──────────────────────────────────────────────────────
// Station resolution + HAFAS API access.
//
// Dual role by design: this service owns the full resolution cascade
// (memory cache → Supabase DB → HAFAS API) so callers get a single
// entry point. Splitting resolution from fetching would just move the
// same dependencies sideways with no architectural benefit.
//
// Hybrid search: local cityStore results fire instantly, then HAFAS
// API results merge in asynchronously for discovery of new stations.

import { apiFetch } from "./api";
import type { City } from "../data/types";
import { getCityById, searchCities } from "./cityStore";
import { extractCityName, haversineKm } from "./geoUtils";

import { supabase } from "./supabaseClient";
import { reportError } from "./errorService";
// ─── HAFAS API Response Types ───────────────────────────────────────────

interface HafasLocation {
    type: "stop" | "station" | "location";
    id?: string;
    name?: string;
    city?: string;
    weight?: number;
    location?: {
        type: string;
        id?: string;
        latitude: number;
        longitude: number;
    };
    products?: {
        nationalExpress?: boolean;
        national?: boolean;
        regional?: boolean;
        suburban?: boolean;
        bus?: boolean;
        ferry?: boolean;
        subway?: boolean;
        tram?: boolean;
        taxi?: boolean;
    };
}


// ─── HAFAS API Search ───────────────────────────────────────────────────

/**
 * Internal: HAFAS location search with an externally-managed AbortSignal.
 * Used by both the simple async API and the streaming callback API.
 */
async function searchHafasWithSignal(query: string, signal: AbortSignal): Promise<City[]> {
    try {
        const response = await apiFetch<HafasLocation[]>(
            "/locations",
            {
                query,
                results: "10",
                stops: "true",
                addresses: "false",
                poi: "false",
                language: "en",
            },
            5 * 60 * 1000, // Cache 5 minutes
            signal
        );

        if (!Array.isArray(response)) return [];

        return response
            .filter(
                (loc) =>
                    (loc.type === "stop" || loc.type === "station") &&
                    loc.id &&
                    Number.isFinite(loc.location?.latitude) &&
                    Number.isFinite(loc.location?.longitude)
            )
            // Filter: Europe Bounding Box (approx)
            // Lat: 35 (Med) to 72 (North Cape)
            // Lon: -25 (Iceland/Portugal) to 45 (East border)
            .filter(
                (loc) =>
                    loc.location!.latitude >= 35 &&
                    loc.location!.latitude <= 72 &&
                    loc.location!.longitude >= -25 &&
                    loc.location!.longitude <= 45
            )
            // Filter: Only keep stations with rail service.
            // Exclude subway-only / tram-only / bus-only stops.
            // If products info is missing, keep the station (benefit of the doubt).
            .filter((loc) => {
                if (!loc.products) return true;
                return !!(loc.products.nationalExpress || loc.products.national || loc.products.regional || loc.products.suburban || loc.products.ferry);
            })
            .map((loc) => hafasLocationToCity(loc));
    } catch (err) {
        if (!signal.aborted) reportError(err, { level: "warning", context: { query } });
        return [];
    }
}

// ─── Hybrid Search (Main Export) ────────────────────────────────────────

/**
 * Search for stations using a streaming hybrid approach:
 * 1. Immediately fires onResults with local Supabase-cached matches
 * 2. Fires again with merged local + HAFAS API results
 *
 * Returns an abort function to cancel the in-flight API request.
 * This pattern gives the UI instant local results while the API resolves.
 */
export function searchStationsLive(
    query: string,
    onResults: (results: City[]) => void
): () => void {
    if (!query || query.trim().length < 2) {
        onResults([]);
        return () => { };
    }

    const localResults = searchCities(query);
    // Fire immediately with local results
    onResults(localResults);

    // Fire API search in background, merge when ready
    let cancelled = false;
    const controller = new AbortController();

    (async () => {
        try {
            const apiResults = await searchHafasWithSignal(query, controller.signal);
            if (cancelled) return;

            if (apiResults.length > 0) {
                // Merge & deduplicate
                const merged = deduplicateCities(localResults, apiResults);
                onResults(merged);
            } else if (localResults.length > 0) {
                onResults(localResults);
            }
        } catch (err) {
            if (cancelled) return;
            reportError(err, { level: "info", context: { query } });
        }
    })();

    return () => {
        cancelled = true;
        controller.abort();
    };
}

/**
 * Deduplicate stations by checking lat/lng proximity.
 * Supabase-cached results (primary) take priority — they're pre-formatted and most-used.
 * HAFAS API results (secondary) are appended only if they don't duplicate a Supabase entry.
 * This prevents flickering and ensures stable, curated results show first.
 */
function deduplicateCities(
    primary: City[],      // Supabase cached results (stable, pre-formatted)
    secondary: City[]     // HAFAS API results (live, for discovery of new stations)
): City[] {
    const result = [...primary];

    for (const apiStation of secondary) {
        // Check if this HAFAS result is a duplicate of something already in Supabase
        const existingIdx = result.findIndex(
            (existing) =>
                existing.id === apiStation.id || sameCity(existing, apiStation)
        );
        if (existingIdx >= 0) {
            // Merge enriched data from API result into the local one
            const existing = result[existingIdx];
            if (!existing.country && apiStation.country) {
                existing.country = apiStation.country;
            }
            if (!existing.population && apiStation.population) {
                existing.population = apiStation.population;
            }
        } else {
            // Genuinely new station — append
            result.push(apiStation);
        }
    }

    // Sort by population descending so capitals / major cities rank first
    result.sort((a, b) => (b.population || 0) - (a.population || 0));

    return result;
}

// ─── HAFAS ID Resolution ────────────────────────────────────────────────

/**
 * Resolve a Station to its numeric HAFAS EVA ID.
 * - If station.id is already a numeric HAFAS ID, returns it as-is.
 * - If station.id is a POI ID (starts with "poi-"), extracts the city_key
 *   and looks it up in the cities table for the main station's HAFAS ID.
 * - If station.id is a city_key (non-numeric), queries /api/locations to find it.
 * Results are cached to avoid repeated lookups.
 *
 * Per CLAUDE.md: always call resolveHafasId() before /api/journeys calls.
 * Fixes: "ghost legs" routing bug caused by POIs having wrong coordinates.
 */
export async function resolveHafasId(city: City, signal?: AbortSignal): Promise<string> {
    signal?.throwIfAborted();
    const known = [city.id, city.hafasId, getCityById(city.id)?.hafasId].find(id => id && /^\d{5,12}$/.test(id));
    if (known) return known;
    const cityKey = city.id.replace(/^poi-/, "");
    let query = supabase.from("cities").select("hafas_station_id").eq("city_key", cityKey);
    if (signal) query = query.abortSignal(signal);
    const { data } = await query.maybeSingle();
    signal?.throwIfAborted();
    if (data?.hafas_station_id && /^\d{5,12}$/.test(data.hafas_station_id)) return data.hafas_station_id;

    const response = await apiFetch<HafasLocation[]>("/locations", {
        query: city.id.startsWith("poi-") ? city.city.trim() : city.name.trim(),
        results: "20", stops: "true", addresses: "false", poi: "false", language: "en",
    }, 10 * 60 * 1000, signal);
    const candidates = (Array.isArray(response) ? response : []).filter(loc =>
        loc.id && /^\d{5,12}$/.test(loc.id) && loc.location &&
        haversineKm(city.latitude, city.longitude, loc.location.latitude, loc.location.longitude) < (city.id.startsWith("poi-") ? 30 : 15)
    );
    const score = (loc: HafasLocation) => (loc.products?.nationalExpress ? 2000 : 0)
        + (loc.products?.national ? 1000 : 0) + (loc.weight ?? 0);
    candidates.sort((a, b) => score(b) - score(a));
    if (!candidates[0]?.id) throw new Error(`No railway station found near ${city.city || city.name}`);
    return candidates[0].id;
}

// ─── Helpers ────────────────────────────────────────────────────────────

/**
 * Convert a HAFAS location response to our City interface.
 * The server has already augmented each stop with `.city` (clean name)
 * and normalised ALL-CAPS `.name` values.
 */
function hafasLocationToCity(loc: HafasLocation & { image_url?: string; description?: string; country?: string; population?: number }): City {
    const id = loc.id || "";
    const rawName = loc.name || "";
    const name = rawName === rawName.toLocaleUpperCase()
      ? rawName.toLocaleLowerCase().replaceAll(/(^|[\s-])\p{L}/gu, letter => letter.toLocaleUpperCase()) : rawName;
    const lat = loc.location?.latitude || 0;
    const lon = loc.location?.longitude || 0;

    const existing = getCityById(id);

    return {
        id,
        name,
        city: existing?.city || loc.city || extractCityName(name),
        country: existing?.country || loc.country || "",
        population: existing?.population || loc.population || 0,
        latitude: lat,
        longitude: lon,
        imageUrl: existing?.imageUrl || loc.image_url || undefined,
        description: existing?.description || loc.description || undefined,
        attractions: existing?.attractions,
        foodSpecialties: existing?.foodSpecialties,
    };
}



// ─── Station Grouping ───────────────────────────────────────────────────
// Groups co-located stations by city and picks the "main" station per group.

export interface StationGroup {
    key: string;
    cityLabel: string;
    /** Local-language alias that matched the search (e.g. "Milano" for Milan) */
    matchedAlias?: string;
    country: string;
    stations: City[];
    /** Best station to use for routing — the main/central station of the city */
    representative: City;
}

/** Extract a normalised city-level key for grouping co-located stations */
function stationGroupKey(city: string): string {
    return extractCityName(city).normalize("NFKC").toLocaleLowerCase().replaceAll(/\s+/g, ' ').trim();
}

function sameCity(a: City, b: City): boolean {
    return stationGroupKey(a.city || a.name) === stationGroupKey(b.city || b.name)
        && (!a.country || !b.country || a.country === b.country)
        && haversineKm(a.latitude, a.longitude, b.latitude, b.longitude) < 15;
}

function isMainStation(city: City): boolean {
    if (!/[-\s]/.test(city.city)) return true;
    return /\b(Hbf|Hauptbahnhof|Hb|Central|Centraal|Centrale|Centralstation|Termini|Sants|Główny|hlavní|Gare|Estació)\b/i.test(city.name);
}

/** Group raw station results by city, pick representative (main/Hbf) per group.
 *  Prefers cities-table entries (non-numeric ID) as the representative so
 *  resolveHafasId() handles station selection at routing time. */
export function groupStations(cities: City[]): StationGroup[] {
    const map = new Map<string, City[]>();
    const order: string[] = [];
    for (const c of cities) {
        const key = order.find(key => sameCity(map.get(key)![0], c)) ?? `${stationGroupKey(c.city)}:${c.id}`;
        if (!map.has(key)) { map.set(key, []); order.push(key); }
        map.get(key)!.push(c);
    }
    return order.map(key => {
        const stns = map.get(key)!;
        stns.sort((a, b) => {
            // Prefer city-level entries (non-numeric IDs from cities table)
            const aNumeric = /^\d+$/.test(a.id) ? 1 : 0;
            const bNumeric = /^\d+$/.test(b.id) ? 1 : 0;
            if (aNumeric !== bNumeric) return aNumeric - bNumeric; // city entries first
            const am = isMainStation(a) ? 0 : 1;
            const bm = isMainStation(b) ? 0 : 1;
            return am - bm || a.name.localeCompare(b.name);
        });
        const cityLabel = stns[0].city || extractCityName(stns[0].name);
        const matchedAlias = stns[0].matchedAlias;
        const representative: City = { ...stns[0], city: cityLabel };
        return { key, cityLabel, matchedAlias, country: stns[0].country || '', stations: stns, representative };
    });
}
